"use client";
import React, { useState } from "react";
import { Box, Plus, Trash2, Code } from "lucide-react";

export const InteractiveFunction = () => {
    const [functionName, setFunctionName] = useState("calculateDiscount");
    const [description, setDescription] = useState("Calculate discount based on user tier and order amount");
    const [isAsync, setIsAsync] = useState(true);
    const [parameters, setParameters] = useState([
        { name: "userId", type: "string", required: true },
        { name: "orderAmount", type: "number", required: true }
    ]);
    const [returnType, setReturnType] = useState("number");

    const paramTypes = ["string", "number", "boolean", "object", "array"];

    const addParameter = () => {
        setParameters([...parameters, { name: "newParam", type: "string", required: false }]);
    };

    const removeParameter = (index: number) => {
        setParameters(parameters.filter((_, i) => i !== index));
    };

    const updateParameter = (index: number, field: string, value: any) => {
        const updated = [...parameters];
        updated[index] = { ...updated[index], [field]: value };
        setParameters(updated);
    };

    return (
        <div className="w-full max-w-2xl mx-auto bg-surface border border-border rounded-xl overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="bg-background/50 backdrop-blur-sm p-4 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-primary/10 rounded-lg">
                        <Box className="w-4 h-4 text-primary" />
                    </div>
                    <span className="font-semibold text-primary-text text-sm">Function Builder</span>
                </div>
                <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500/20 border border-red-500/50" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/20 border border-yellow-500/50" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500/20 border border-green-500/50" />
                </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6 bg-gradient-to-b from-surface to-background max-h-[600px] overflow-y-auto">
                {/* Function Name */}
                <div className="space-y-2">
                    <label className="text-xs font-medium text-secondary-text uppercase tracking-wider">
                        Function Name
                    </label>
                    <input
                        type="text"
                        value={functionName}
                        onChange={(e) => setFunctionName(e.target.value)}
                        className="w-full bg-background border border-border rounded-lg p-3 text-sm text-primary-text font-mono focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
                    />
                </div>

                {/* Description */}
                <div className="space-y-2">
                    <label className="text-xs font-medium text-secondary-text uppercase tracking-wider">
                        Description
                    </label>
                    <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="w-full bg-background border border-border rounded-lg p-3 text-sm text-primary-text resize-none focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
                        rows={2}
                    />
                </div>

                {/* Async Toggle */}
                <div className="flex items-center justify-between p-4 bg-background/50 rounded-lg border border-border">
                    <div className="flex items-center gap-3">
                        <Code className="w-5 h-5 text-primary" />
                        <div>
                            <div className="text-sm font-semibold text-primary-text">Async Function</div>
                            <div className="text-xs text-secondary-text">
                                {isAsync ? "Returns a Promise" : "Synchronous execution"}
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={() => setIsAsync(!isAsync)}
                        className={`w-11 h-6 rounded-full relative transition-colors ${isAsync ? "bg-primary" : "bg-border"
                            }`}
                    >
                        <div
                            className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-transform ${isAsync ? "left-6" : "left-1"
                                }`}
                        />
                    </button>
                </div>

                {/* Parameters */}
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <label className="text-xs font-medium text-secondary-text uppercase tracking-wider">
                            Input Parameters
                        </label>
                        <button
                            onClick={addParameter}
                            className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
                        >
                            <Plus className="w-3 h-3" />
                            Add Parameter
                        </button>
                    </div>
                    <div className="space-y-2">
                        {parameters.map((param, index) => (
                            <div
                                key={index}
                                className="p-3 bg-background border border-border rounded-lg space-y-2 group hover:border-primary/30 transition-all"
                            >
                                <div className="flex items-center gap-2">
                                    <input
                                        type="text"
                                        value={param.name}
                                        onChange={(e) => updateParameter(index, "name", e.target.value)}
                                        className="flex-1 bg-surface border border-border rounded px-2 py-1 text-xs font-mono text-primary-text focus:outline-none focus:ring-1 focus:ring-primary/50"
                                        placeholder="paramName"
                                    />
                                    <select
                                        value={param.type}
                                        onChange={(e) => updateParameter(index, "type", e.target.value)}
                                        className="bg-surface border border-border rounded px-2 py-1 text-xs text-primary-text focus:outline-none focus:ring-1 focus:ring-primary/50"
                                    >
                                        {paramTypes.map((type) => (
                                            <option key={type} value={type}>
                                                {type}
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        onClick={() => removeParameter(index)}
                                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                        <Trash2 className="w-4 h-4 text-red-500 hover:text-red-400" />
                                    </button>
                                </div>
                                <label className="flex items-center gap-2 text-xs text-secondary-text cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={param.required}
                                        onChange={(e) => updateParameter(index, "required", e.target.checked)}
                                        className="rounded border-border text-primary focus:ring-primary/50"
                                    />
                                    Required parameter
                                </label>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Return Type */}
                <div className="space-y-2">
                    <label className="text-xs font-medium text-secondary-text uppercase tracking-wider">
                        Return Type
                    </label>
                    <select
                        value={returnType}
                        onChange={(e) => setReturnType(e.target.value)}
                        className="w-full bg-background border border-border rounded-lg p-3 text-sm text-primary-text focus:outline-none focus:ring-2 focus:ring-primary/50"
                    >
                        {paramTypes.map((type) => (
                            <option key={type} value={type}>
                                {type}
                            </option>
                        ))}
                        <option value="void">void</option>
                    </select>
                </div>

                {/* Preview */}
                <div className="p-4 bg-background/80 rounded-lg border border-border/50">
                    <div className="text-xs font-medium text-secondary-text uppercase tracking-wider mb-2">
                        Function Signature
                    </div>
                    <div className="font-mono text-xs text-primary-text">
                        <span className="text-purple-400">{isAsync ? "async " : ""}</span>
                        <span className="text-green-400">{functionName}</span>
                        <span className="text-secondary-text">(</span>
                        {parameters.map((param, i) => (
                            <span key={i}>
                                <span className="text-blue-400">{param.name}</span>
                                <span className="text-secondary-text">: </span>
                                <span className="text-amber-400">{param.type}</span>
                                {i < parameters.length - 1 && <span className="text-secondary-text">, </span>}
                            </span>
                        ))}
                        <span className="text-secondary-text">): </span>
                        <span className="text-amber-400">{isAsync ? `Promise<${returnType}>` : returnType}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};
